import type { ParamParseKey } from 'react-router-dom';
import { generatePath, matchPath } from 'react-router-dom';

import { Sport } from '__generated__/globalTypes';
import {
  ANY_SPORT_MARKET_COMMON_PACKS,
  ANY_SPORT_MARKET_SECONDARY,
  ANY_SPORT_TEAMS_SLUG,
  FOOTBALL,
  FOOTBALL_CLUBS_SLUG,
  FOOTBALL_MARKET_MANAGER_SALES,
  FOOTBALL_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER,
  FOOTBALL_PLAY_EVENTTYPE_MY_LINEUPS_ID,
  FOOTBALL_SCOUTING_CENTER_MATCHES_DAY_GAMEID_LINEUPS,
  MLB,
  MLB_MARKET_COMMON_MARKET,
  MLB_MARKET_SECONDARY,
  MLB_PLAY_EVENTTYPE_FIXTURE_GAME_CENTER,
  MLB_PLAY_EVENTTYPE_LINEUPS_ID,
  MLB_SCOUTING_CENTER_MATCHES_DAY_GAMEID_BOX_SCORE,
  MLB_TEAMS_SLUG,
  NBA,
  NBA_MARKET_SECONDARY,
  NBA_PLAY_EVENTTYPE_FIXTURE_GAME_CENTER,
  NBA_PLAY_EVENTTYPE_LINEUPS_ID,
  NBA_SCOUTING_CENTER_MATCHES_DAY_GAMEID_BOX_SCORE,
  NBA_TEAMS_SLUG,
} from 'constants/__generated__/routes';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER,
  ANY_SPORT_PLAY_EVENTTYPE_MY_LINEUPS_LINEUP,
  ANY_SPORT_SCOUTING_CENTER_MATCHES_DAY_GAMEID_BOX_SCORE,
} from 'constants/routes';

export const SPORT_PATH_MAP = {
  [Sport.FOOTBALL]: FOOTBALL,
  [Sport.BASEBALL]: MLB,
  [Sport.NBA]: NBA,
};

/**
 * Rewrite paths that are different between sports
 * should be temporary and removed when paths are cross-sport consistent
 */
const UNMATCHED_CROSS_SPORT_PATHS = {
  [ANY_SPORT_MARKET_SECONDARY]: {
    [Sport.FOOTBALL]: FOOTBALL_MARKET_MANAGER_SALES,
    [Sport.BASEBALL]: MLB_MARKET_SECONDARY,
    [Sport.NBA]: NBA_MARKET_SECONDARY,
  },
  [ANY_SPORT_PLAY_EVENTTYPE_MY_LINEUPS_LINEUP]: {
    [Sport.FOOTBALL]: FOOTBALL_PLAY_EVENTTYPE_MY_LINEUPS_ID,
    [Sport.BASEBALL]: MLB_PLAY_EVENTTYPE_LINEUPS_ID,
    [Sport.NBA]: NBA_PLAY_EVENTTYPE_LINEUPS_ID,
  },
  [ANY_SPORT_TEAMS_SLUG]: {
    [Sport.FOOTBALL]: FOOTBALL_CLUBS_SLUG,
    [Sport.BASEBALL]: MLB_TEAMS_SLUG,
    [Sport.NBA]: NBA_TEAMS_SLUG,
  },
  [ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER]: {
    [Sport.FOOTBALL]: FOOTBALL_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER,
    [Sport.BASEBALL]: MLB_PLAY_EVENTTYPE_FIXTURE_GAME_CENTER,
    [Sport.NBA]: NBA_PLAY_EVENTTYPE_FIXTURE_GAME_CENTER,
  },
  [ANY_SPORT_MARKET_COMMON_PACKS]: {
    [Sport.FOOTBALL]: `/football${ANY_SPORT_MARKET_COMMON_PACKS}`,
    [Sport.BASEBALL]: MLB_MARKET_COMMON_MARKET,
    [Sport.NBA]: `/nba${ANY_SPORT_MARKET_COMMON_PACKS}`,
  },
  [ANY_SPORT_SCOUTING_CENTER_MATCHES_DAY_GAMEID_BOX_SCORE]: {
    [Sport.FOOTBALL]: FOOTBALL_SCOUTING_CENTER_MATCHES_DAY_GAMEID_LINEUPS,
    [Sport.BASEBALL]: MLB_SCOUTING_CENTER_MATCHES_DAY_GAMEID_BOX_SCORE,
    [Sport.NBA]: NBA_SCOUTING_CENTER_MATCHES_DAY_GAMEID_BOX_SCORE,
  },
} as const;

type UnmachedCrossSportPaths = typeof UNMATCHED_CROSS_SPORT_PATHS;
type UnmachedCrossSportPath<Path extends keyof UnmachedCrossSportPaths> =
  UnmachedCrossSportPaths[Path];
type ParseParamKeysFromUnmatchedPaths<Entry> = Entry extends {
  [Sport.FOOTBALL]: infer A extends string;
  [Sport.BASEBALL]: infer B extends string;
  [Sport.NBA]: infer C extends string;
}
  ? ParamParseKey<A | B | C>
  : never;

export type PathParams<Path extends string> =
  Path extends keyof typeof UNMATCHED_CROSS_SPORT_PATHS
    ? {
        [key in ParseParamKeysFromUnmatchedPaths<
          UnmachedCrossSportPath<Path>
        >]: string;
      }
    : {
        [key in ParamParseKey<Path>]: string;
      };

const isUnmatchedCrossSportPath = (
  path: string
): path is keyof typeof UNMATCHED_CROSS_SPORT_PATHS =>
  Object.keys(UNMATCHED_CROSS_SPORT_PATHS).includes(path);

export const generateSportPath = <Path extends string>(
  path: Path,
  {
    params,
    sport,
  }: {
    params?: PathParams<Path>;
    sport: Sport;
  }
) => {
  if (Object.values(SPORT_PATH_MAP).some(appPath => path.startsWith(appPath))) {
    return generatePath(path, params);
  }
  if (isUnmatchedCrossSportPath(path)) {
    return generatePath(
      UNMATCHED_CROSS_SPORT_PATHS[path][sport || Sport.FOOTBALL] as string,
      params
    );
  }
  return generatePath(
    `${sport ? SPORT_PATH_MAP[sport] : ''}${path.startsWith('/') ? '' : '/'}${path as string}`,
    {
      sport,
      ...params,
    }
  );
};

export const matchAnySportPath = (
  pattern: string,
  pathname: string,
  sport?: Sport
) => {
  if (isUnmatchedCrossSportPath(pattern) && sport) {
    return matchPath(UNMATCHED_CROSS_SPORT_PATHS[pattern][sport], pathname);
  }

  return matchPath(
    pattern,
    Object.values(SPORT_PATH_MAP).reduce(
      (acc, path) => acc.replace(new RegExp(`^${path}`, 'g'), ''),
      pathname
    )
  );
};
